/**
 * PWA UI 字串目錄 — 繁體中文（來源語言 / source of truth）。
 *
 * 本檔之 key 集合即 `TranslationKey`；`en.ts` / `ja.ts` 必須提供同一組 key
 * （由 `Record<TranslationKey, string>` 型別強制）。
 *
 * 插值：字串內 `{name}` 佔位，由 `i18n.ts` 之 `translate()` 以 params 取代。
 */
export const zhTW = {
  // ── common ───────────────────────────────────────────────
  'common.back': '返回',
  'common.backHome': '返回首頁',
  'common.loading': '載入中…',
  'common.unknownError': '未知錯誤',
  'common.logout': '登出',
  'common.logoutPending': '登出中…',

  // ── login ────────────────────────────────────────────────
  'login.brand': '遊艇票務',
  'login.title': '登入',
  'login.emailPlaceholder': 'you@example.com',
  'login.email': 'Email',
  'login.password': '密碼',
  'login.submit': '登入',
  'login.submitting': '登入中…',
  'login.err2fa': '此帳號需 2FA 驗證；目前 scaffold 暫未提供 TOTP 流程',
  'login.errBadCredentials': '帳號或密碼錯誤',
  'login.errGeneric': '登入失敗，請稍後再試',

  // ── home ─────────────────────────────────────────────────
  'home.title': '遊艇商品',
  'home.navMyOrders': '我的訂單',
  'home.navMyRefunds': '我的退票',
  'home.navRecommend': '行程推薦',
  'home.myTicketsHeading': '我的票券（最近 {count} 張）',
  'home.ticketsCapNote': '僅顯示前 10 張；最多保留 50 張（最新優先；本機保存）',
  'home.orderRef': '訂單 {id}',
  'home.loadFailed': '無法載入商品：',
  'home.empty': '目前無可預訂商品。',
  'home.noMatch': '無符合條件的商品；請調整搜尋關鍵字或分類。',
  'home.searchPlaceholder': '搜尋商品名稱或編號…',
  'home.allCategories': '全部分類',
  'home.unpublished': '（未上架）',
  'home.buyNow': '立即購買',
  'home.durationMin': '· {minutes} 分鐘',

  // ── product detail ───────────────────────────────────────
  'product.title': '商品詳情',
  'product.loadFailed': '無法載入商品：',
  'product.durationLabel': '· 時長 {minutes} 分鐘',
  'product.skuLabel': 'SKU：',
  'product.categoryLabel': '類別：',
  'product.chooseSchedule': '選擇班次',
  'product.unpublished': '未上架',

  // ── buy / schedule ───────────────────────────────────────
  'buy.title': '選擇班次',
  'buy.unitPrice': '· 單價 {price}',
  'buy.loadingSchedules': '載入班次中…',
  'buy.scheduleLoadFailed': '無法載入班次：',
  'buy.noSchedules': '本商品目前無可預訂班次。',
  'buy.until': '迄 {time}',
  'buy.cancelled': '已停航',
  'buy.remaining': '剩餘 {remaining} / {capacity} 座',
  'buy.soldOut': '已售完',
  'buy.quantity': '數量',
  'buy.subtotal': '小計：',
  'buy.originalTotal': '原價：',
  'buy.promoSaved': '優惠折抵：',
  'buy.orderFailed': '下單失敗：',
  'buy.submit': '下單',
  'buy.submitting': '建立訂單中…',
  'buy.pkgLead': '本套票包含以下體驗，請分別選擇時間',
  'buy.pkgSegment': '第 {n} 段',
  'buy.pkgPickEach': '請為每一段選擇時間',
  'buy.pkgNoSlot': '此段目前沒有可選的時間',
  'buy.pkgClash': '與已選時段重疊',
  'buy.pkgNotSellable': '此套票尚未開放購買',
  'buy.pkgLimit': '本套票最多可購買 {n} 人（受各段名額限制）',
  'buy.minutes': '分鐘',

  // ── checkout ─────────────────────────────────────────────
  'checkout.title': '付款',
  'checkout.orderNo': '訂單編號：',
  'checkout.demoNote':
    'demo 模式 — 本按鈕將直接呼叫後端 mock payment-callback，將訂單由 HOLD → PAYMENT_PENDING → PAID，並於同 transaction 內發出對應票券。',
  'checkout.reqFailed': '付款請求失敗：',
  'checkout.retryHint': '。請再試一次。',
  'checkout.pending': '處理中…',
  'checkout.retry': '重試付款',
  'checkout.pay': '模擬付款成功',
  'checkout.oversoldTitle': '班次已售罄',
  'checkout.oversoldLine1': '很抱歉，此班次在付款的瞬間已被其他旅客訂滿，訂單無法成立。',
  'checkout.oversoldLine2':
    '您的款項將自動原路退回，我們會以通知告知退款進度，您無須另外申請退票。',
  'checkout.failedTitle': '付款未成功',
  'checkout.failedLine1': '這筆付款未能完成，訂單未成立。',
  'checkout.failedLine2': '若款項已遭扣除將自動退回；請返回首頁重新下單。',
  'checkout.orderState': '訂單 {id} · 狀態：{state}',
  'checkout.backReselect': '返回首頁重新挑選',

  // ── order confirm ────────────────────────────────────────
  'confirm.title': '訂單完成',
  'confirm.orderLabel': '訂單',
  'confirm.statusInline': '狀態：{state}',
  'confirm.statusLabel': '狀態',
  'confirm.paidAmountLabel': '付款金額',
  'confirm.paidAmount': '付款金額：',
  'confirm.noTickets': '未取得票券清單；若您是直接連入本頁，請從 Email 或訂單列表進入個別票券。',
  'confirm.issuedHeading': '已發行票券（{count} 張）',
  'confirm.viewQr': '查看 QR · {id}',

  // ── ticket QR ────────────────────────────────────────────
  'ticket.title': '票券 QR Code',
  'ticket.loadFailed': '無法載入票券：',
  'ticket.refundLocked': '已鎖退款',
  'ticket.refundEligible': '可退款',
  'ticket.qrNote': '請於現場將 QR Code 出示給服務人員掃描；本 QR 含密碼學簽章，可防偽造。',
  'ticket.applyRefund': '申請退票',

  // ── refund request ───────────────────────────────────────
  'refundReq.title': '申請退票',
  'refundReq.backToTicket': '返回票券',
  'refundReq.loadFailed': '無法載入票券：',
  'refundReq.ticketLabel': '票券：',
  'refundReq.statusInline': '狀態：{state}',
  'refundReq.cannotRefundState': '此票券狀態為 {state}，不可申請退票。',
  'refundReq.lockedWindow': '已過退票窗口（REFUND_LOCKED），無法線上申請退票。',
  'refundReq.reasonLabel': '退票理由（1–500 字）',
  'refundReq.reasonPlaceholder': '請說明退票原因，將提供業務參考。',
  'refundReq.charCount': '{count} / 500',
  'refundReq.submitFailed': '送出失敗：',
  'refundReq.submit': '送出退票申請',
  'refundReq.submitting': '送出中…',
  'refundReq.footnote': '退款金額將以原訂單明細為準；送出後將進入退款流程，登船將被即時阻擋。',

  // ── refund detail ────────────────────────────────────────
  'refundDetail.title': '退票進度',
  'refundDetail.loadFailed': '無法載入退票紀錄：',
  'refundDetail.refundNo': '退票編號：',
  'refundDetail.refundStatus': '退款狀態：',
  'refundDetail.ticketStatus': '票券狀態：',
  'refundDetail.refundAmount': '退款金額：',
  'refundDetail.allowancePending': '款項已退回，發票折讓尚在處理中。',
  'refundDetail.manualReview': '本筆退票已進入人工複核，將於 24 小時內由專員處理。',
  'refundDetail.pollingNote': '進度將自動更新（10 秒）；終態後停止輪詢。',
  'refundDetail.viewTicket': '查看對應票券',

  // ── my orders ────────────────────────────────────────────
  'myOrders.title': '我的訂單',
  'myOrders.empty': '尚無訂單紀錄。',
  'myOrders.snapshotNote': '狀態 / 金額為訂單成立當下之快照；本機保存最多 50 筆。',

  // ── my refunds ───────────────────────────────────────────
  'myRefunds.title': '我的退票',
  'myRefunds.empty': '尚無退票申請紀錄。',
  'myRefunds.ticketRef': '票券 {id}',
  'myRefunds.rowLoadFailed': '無法載入',

  // ── order detail（D）──────────────────────────────────────
  'orderDetail.title': '訂單詳情',
  'orderDetail.loadFailed': '無法載入訂單：',
  'orderDetail.orderNumber': '訂單編號：',
  'orderDetail.statusLabel': '訂單狀態：',
  'orderDetail.channelLabel': '通路：',
  'orderDetail.subtotal': '小計：',
  'orderDetail.total': '總計：',
  'orderDetail.createdAt': '成立時間：',
  'orderDetail.holdExpiresAt': '保留至：',

  // ── account（F）──────────────────────────────────────────
  'account.title': '帳號資訊',
  'account.loadFailed': '無法載入帳號資訊。',
  'account.displayName': '顯示名稱',
  'account.email': 'Email',
  'account.userId': '使用者 ID',
  'account.permissions': '權限',
  'account.permissionsCount': '{count} 項',
  'account.twoFactor': '兩階段驗證',
  'account.twoFactorOn': '已啟用',
  'account.twoFactorOff': '未啟用',
  'account.readonlyNote': '此頁為唯讀；如需修改帳號資料請洽客服。',

  // ── 補充（D/E/F 共用）────────────────────────────────────
  'home.navAccount': '帳號',
  'ticket.canBoard': '可登船',
  'ticket.cannotBoard': '暫不可登船',
} as const;

/** 全部 UI 字串 key — en / ja 目錄須完整提供同一組。 */
export type TranslationKey = keyof typeof zhTW;
